// src/theme.ts
import { extendTheme } from "@chakra-ui/react";
const API_BASE_URL = import.meta.env.VITE_BASE_URL || "https://localhost:5001";

const theme = extendTheme({
  config: {
    initialColorMode: "dark",
    useSystemColorMode: false,
  },
  fonts: {
    heading: "'DM Sans', sans-serif",
    body: "'DM Sans', sans-serif",
  },
  styles: {
    global: (props: any) => ({
      html: {
      },
      body: {
        background: props.colorMode === "dark"
          ? "radial-gradient(circle at 75% 80%, rgba(94, 234, 212, 0.4), rgba(2, 0, 36, 0.95))"
          : "radial-gradient(ellipse at 50% 40%, rgba(255,252,240,.55), transparent 70%), radial-gradient(ellipse at 50% 120%, rgba(120,90,50,.12), transparent 60%), #fbf7ee", // light = parchment (see styles/paper.css)
        backgroundAttachment: "fixed",
        backgroundPosition: "center",
        color: props.colorMode === "dark" ? "gray.100" : "#1c1813",
        margin: 0,
        padding: 0,
        boxSizing: "border-box",
      },
      "#root": {
      },
    }),
  },
  textures: {
    backgroundImage: `${API_BASE_URL}/assets/images/textures/glucose.png`, // ensure it's in your public folder
  },
  colors: {
    brand: {
      100: "#CFFAFE",
      500: "#06b6d4",
      900: "#0e7490",
    },
  },
  semanticTokens: {
    colors: {
      cardGradient: {
        _light: "linear-gradient(180deg, #fffdf8 0%, #fbf6ea 100%)",
        _dark: "radial-gradient(circle at 70% 70%, rgba(72, 187, 215, 0.25), rgba(45, 55, 72, 1))",
      },
      stackGradient: {
        _light: "linear-gradient(180deg, #fffdf8 0%, #fcf8ef 100%)",
        _dark: "radial-gradient(circle at 75% 80%, rgba(136, 230, 196, 0.31), rgb(2, 0, 36))",
      },
      statGradient: {
        _light: "linear-gradient(180deg, #fffdf8 0%, #f8f2e4 100%)",
        _dark: "radial-gradient(circle at bottom left, rgba(0, 255, 225, 0.05) 0%, #0a192f 20%, #010212 50%, #000000 80%)",
      },
      stat2Gradient: {
        _light: "linear-gradient(180deg, #fdfaf3, #f6efdf)",
        _dark: "linear-gradient(to top right, rgba(0, 100, 90, 1), rgba(0, 0, 0, 1))",
      },
      cardTeal: {
        _light: "#7a1f1a",
        _dark: "teal.400",
      },
      cardBlue: {
        _light: "#263555",
        _dark: "#4682b4",
      },
      cardGreen: {
        _light: "#2f5d2a",
        _dark: "#175d48",
      },
      cardBlue2: {
        _light: "#263555",
        _dark: "#212e4d",
      },
    },
  },
  components: {
    Modal: {
      baseStyle: (props: any) => ({
        overlay: {
          bg: props.colorMode === "dark" ? "rgba(0, 0, 0, 0.6)" : "rgba(28, 24, 19, 0.35)",
        },
        dialog: {
          bg: props.colorMode === "dark"
            ? "rgba(15, 23, 42, 0.95)"
            : "#fdfaf3",
          ...(props.colorMode === "dark" ? {} : { bgImage: "var(--paper-curl-xl)", bgRepeat: "no-repeat", bgAttachment: "local", color: "#1c1813" }),
          borderRadius: "2xl",
          border: "2px solid",
          borderColor: props.colorMode === "dark"
            ? "rgba(94, 234, 212, 0.5)"
            : "rgba(28, 24, 19, 0.28)",
          boxShadow: props.colorMode === "dark"
            ? "0 25px 50px -12px rgba(0, 255, 255, 0.5), 0 0 0 1px rgba(94, 234, 212, 0.3)"
            : "0 1px 0 rgba(28, 24, 19, 0.05), 0 30px 60px -30px rgba(60, 42, 22, 0.55)",
        },
        header: {
          color: props.colorMode === "dark" ? "cyan.300" : "#7a1f1a",
          fontWeight: "bold",
          borderBottom: "1px solid",
          borderColor: props.colorMode === "dark"
            ? "rgba(94, 234, 212, 0.2)"
            : "rgba(28, 24, 19, 0.16)",
          pb: 4,
        },
        body: {
          color: props.colorMode === "dark" ? "gray.100" : "#1c1813",
        },
        footer: {
          borderTop: "1px solid",
          borderColor: props.colorMode === "dark"
            ? "rgba(94, 234, 212, 0.2)"
            : "rgba(28, 24, 19, 0.16)",
          pt: 4,
        },
      }),
      sizes: {
        // Custom z-index layers for different modal types
        claimLink: {
          dialog: {
            maxW: "600px",
            zIndex: 10000,
          },
          overlay: {
            zIndex: 9999,
            bg: "rgba(0, 0, 0, 0.75)",
          },
        },
        claimDetail: {
          dialog: {
            zIndex: 9000, // High - Claim detail modals
          },
        },
        standard: {
          dialog: {
            zIndex: 1400, // Standard modals
          },
        },
      },
    },
    Card: {
      baseStyle: (props: any) => ({
        container: {
          background: props.colorMode === "dark"
            ? "radial-gradient(circle at 70% 70%, rgba(72, 187, 215, 0.25), rgba(45, 55, 72, 1))"
            : "var(--paper-curl-lg) no-repeat, linear-gradient(180deg, #fffdf8 0%, #fbf6ea 100%)",
          borderRadius: "2xl",
          boxShadow: props.colorMode === "dark"
            ? "lg"
            : "var(--paper-lift)",
          border: "1px solid",
          borderColor: props.colorMode === "dark" ? "gray.700" : "rgba(28, 24, 19, 0.16)",
          backdropFilter: props.colorMode === "dark" ? "blur(12px)" : "none",
        },
      }),
    },
    Heading: {
      baseStyle: (props: any) => ({
        fontWeight: "bold",
        color: props.colorMode === "dark" ? "teal.300" : "#1c1813",
      }),
    },
    Text: {
      baseStyle: (props: any) => ({
        color: props.colorMode === "dark" ? "gray.100" : "#2a241c",
      }),
    },
    // Light mode = print on paper: controls get a small page curl, panels a larger one.
    Button: {
      baseStyle: () => ({
        fontWeight: "600",
      }),
      variants: {
        solid: (props: any) => (props.colorMode === "dark" ? {} : {
          bgImage: "var(--paper-curl-sm)", bgRepeat: "no-repeat",
          ...(props.colorScheme === "gray" ? {} : { color: "#fdf9ef" }),
        }),
        outline: (props: any) => (props.colorMode === "dark" ? {} : {
          bgImage: "var(--paper-curl-sm)", bgRepeat: "no-repeat", borderColor: "rgba(28, 24, 19, 0.35)",
        }),
      },
    },
    Drawer: {
      baseStyle: (props: any) => (props.colorMode === "dark" ? {} : {
        dialog: {
          bg: "#fdfaf3", color: "#1c1813",
          bgImage: "var(--paper-curl-xl)", bgRepeat: "no-repeat", bgAttachment: "local",
          boxShadow: "-18px 0 40px -24px rgba(60, 42, 22, 0.45)",
        },
        overlay: { bg: "rgba(28, 24, 19, 0.3)" },
      }),
    },
    Input: {
      variants: {
        outline: (props: any) => (props.colorMode === "dark" ? {} : {
          field: { bg: "rgba(255, 250, 236, 0.7)", borderColor: "rgba(28, 24, 19, 0.25)", _placeholder: { color: "#8a7d66" } },
        }),
      },
    },
    Textarea: {
      variants: {
        outline: (props: any) => (props.colorMode === "dark" ? {} : { bg: "rgba(255, 250, 236, 0.7)", borderColor: "rgba(28, 24, 19, 0.25)" }),
      },
    },
    Select: {
      variants: {
        outline: (props: any) => (props.colorMode === "dark" ? {} : {
          field: { bg: "rgba(255, 250, 236, 0.7)", bgImage: "var(--paper-curl-sm)", bgRepeat: "no-repeat", borderColor: "rgba(28, 24, 19, 0.25)" },
        }),
      },
    },
  },
});

export default theme;
